import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { apiUrl } from '@/lib/env';

interface AuthMethods {
  authMode: 'local' | 'bitrix24';
  methods: Array<'bitrix24' | 'local'>;
  localAuthAvailable: boolean;
}

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const authError = searchParams.get('authError');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [authMethods, setAuthMethods] = useState<AuthMethods | null>(null);

  useEffect(() => {
    fetch(apiUrl('/auth/methods'), { credentials: 'include' })
      .then((res) => res.json())
      .then((data: AuthMethods) => setAuthMethods(data))
      .catch(() =>
        setAuthMethods({ authMode: 'bitrix24', methods: ['bitrix24'], localAuthAvailable: false }),
      );
  }, []);

  useEffect(() => {
    if (authError) {
      const messages: Record<string, string> = {
        bitrix_disabled: 'Аутентификация через Bitrix24 отключена (AUTH_MODE≠bitrix24)',
        missing_code: 'Не получен код авторизации от Bitrix24',
        inactive_user: 'Учётная запись пользователя неактивна',
        bitrix_env_missing: 'Ошибка конфигурации сервера',
        bitrix_oauth_failed: 'Не удалось выполнить вход через Bitrix24',
      };
      setErrorMessage(messages[authError] ?? 'Произошла ошибка при входе');
    }
  }, [authError]);

  const handleBitrixLogin = useCallback(() => {
    window.location.assign(apiUrl('/auth/bitrix/start'));
  }, []);

  const showBitrix = authMethods?.methods.includes('bitrix24') ?? true;
  const showLocalLink = authMethods?.localAuthAvailable === true;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        background: 'var(--background)',
      }}
    >
      <div
        style={{
          maxWidth: '420px',
          width: '100%',
          padding: '48px 32px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '8px',
            }}
          >
            AXIUM meet
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            Платформа транскрибации и диаризации
          </p>
        </div>

        {errorMessage && (
          <div
            role="alert"
            style={{
              padding: '12px 16px',
              marginBottom: '24px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '14px',
            }}
          >
            {errorMessage}
          </div>
        )}

        {showBitrix ? (
          <>
            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Для доступа к платформе требуется аутентификация через Bitrix24
              </p>
              <Button variant="primary" onClick={handleBitrixLogin} style={{ width: '100%' }}>
                Войти через Bitrix24
              </Button>
            </div>

            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
              После нажатия вы будете перенаправлены на портал Bitrix24 для входа
            </div>
          </>
        ) : (
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Основной вход через Bitrix24 недоступен. Используйте резервный вход.
          </p>
        )}

        {showLocalLink && (
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <Link
              to="/login/local"
              style={{
                color: 'var(--text-tertiary)',
                fontSize: '12px',
                textDecoration: 'underline',
              }}
            >
              Вход по логину и паролю
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
